import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createMedida } from '../api/antropometriaApi';
import {
  calculateImc,
  calculatePerimetroMedio,
  calculatePesoBrazosPiernas,
  calculateSmi,
  toNumber,
  toFixedString,
} from '../utils/antropometriaCalculations';

const DECIMAL_FIELDS = [
  'peso',
  'grasa',
  'musculo',
  'bd',
  'bi',
  'pd',
  'pi',
  'perimetroAbdominal1',
  'perimetroAbdominal2',
  'perimetroAbdominal3',
  'talla',
];

const INTEGER_FIELDS = ['nivelGrasa'];

const initialDatos = {
  talla: '',
  peso: '',
  perimetroAbdominal1: '',
  perimetroAbdominal2: '',
  perimetroAbdominal3: '',
  grasa: '',
  nivelGrasa: '',
  musculo: '',
  bd: '',
  bi: '',
  pd: '',
  pi: '',
};

const validarNumeroDecimal = (valor) => /^[0-9]+([,.][0-9]{0,2})?$/.test(valor);
const validarEntero = (valor) => /^[0-9]+$/.test(valor);

const normalizeInput = (value) => value.trim().replace('.', ',');

export const useAntropometriaForm = ({ idPaciente }) => {
  const navigate = useNavigate();
  const [datos, setDatos] = useState(initialDatos);
  const [errores, setErrores] = useState({});
  const [notificacion, setNotificacion] = useState({ open: false, tipo: '', mensaje: '' });

  const computed = useMemo(() => {
    const imc = calculateImc({ peso: datos.peso, tallaCm: datos.talla, asComma: true });
    const mediaPerimetroAbdominal = calculatePerimetroMedio({
      p1: datos.perimetroAbdominal1,
      p2: datos.perimetroAbdominal2,
      p3: datos.perimetroAbdominal3,
      asComma: true,
    });
    const pesoBrazosPiernas = calculatePesoBrazosPiernas({
      bd: datos.bd,
      bi: datos.bi,
      pd: datos.pd,
      pi: datos.pi,
      asComma: true,
    });
    const smi = calculateSmi({
      bd: datos.bd,
      bi: datos.bi,
      pd: datos.pd,
      pi: datos.pi,
      tallaCm: datos.talla,
      asComma: true,
    });

    return {
      imc,
      mediaPerimetroAbdominal,
      pesoBrazosPiernas,
      smi,
    };
  }, [datos]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    const nextValue = normalizeInput(value);
    let error = '';

    if (DECIMAL_FIELDS.includes(name) && nextValue !== '' && !validarNumeroDecimal(nextValue)) {
      error = 'Debe ser un número con hasta dos decimales.';
    }

    if (INTEGER_FIELDS.includes(name) && nextValue !== '' && !validarEntero(nextValue)) {
      error = 'Debe ser un número entero.';
    }

    setErrores((prev) => ({ ...prev, [name]: error }));
    if (!error) {
      setDatos((prev) => ({ ...prev, [name]: nextValue }));
    }
  }, []);

  const closeNotification = () => setNotificacion((prev) => ({ ...prev, open: false }));

  const guardarDatos = async () => {
    if (Object.values(errores).some((err) => err)) {
      setNotificacion({
        open: true,
        tipo: 'error',
        mensaje: 'Corrige los errores antes de guardar.',
      });
      return;
    }

    try {
      const userId = localStorage.getItem('id');
      await createMedida({
        idPaciente,
        userId,
        payload: {
          talla: toFixedString(datos.talla, 2),
          peso: toFixedString(datos.peso, 2),
          perimetroAbdominal1: toFixedString(datos.perimetroAbdominal1, 2),
          perimetroAbdominal2: toFixedString(datos.perimetroAbdominal2, 2),
          perimetroAbdominal3: toFixedString(datos.perimetroAbdominal3, 2),
          mediaPerimetroAbdominal: toFixedString(computed.mediaPerimetroAbdominal, 2),
          grasa: toFixedString(datos.grasa, 1),
          nivelGrasa: parseInt(datos.nivelGrasa, 10),
          musculo: Number.isNaN(toNumber(datos.musculo)) ? 0 : toNumber(datos.musculo),
          bd: toFixedString(datos.bd, 2),
          bi: toFixedString(datos.bi, 2),
          pd: toFixedString(datos.pd, 2),
          pi: toFixedString(datos.pi, 2),
          pesoBrazosPiernas: toFixedString(computed.pesoBrazosPiernas, 2),
          smi: toFixedString(computed.smi, 2),
          imc: toFixedString(computed.imc, 2),
        },
      });

      setNotificacion({
        open: true,
        tipo: 'success',
        mensaje: '¡Datos guardados correctamente!',
      });
      setTimeout(() => {
        navigate(`/paciente/${idPaciente}`);
      }, 1500);
    } catch (error) {
      console.error('Error al guardar datos:', error);
      setNotificacion({
        open: true,
        tipo: 'error',
        mensaje: 'No hay pacientes con ese código.',
      });
    }
  };

  return {
    datos,
    errores,
    notificacion,
    computed,
    handleChange,
    closeNotification,
    guardarDatos,
  };
};
